import React from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Route, Switch } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { ThemeProvider } from 'wtb-components/theme';

import { registerLanguage, registerLanguages } from 'wtb-utils';
import { main as theme } from 'themes';
import {
  AppProvider,
  AutogiroProvider,
  BetalingsserviceProvider,
  SignProvider,
  BillingProvider,
  DialogNavigationProvider,
  LocaleProvider,
  PaymentProvider,
  TransactionProvider,
  SepaProvider,
  AuthProvider
} from 'context';
import {
  Download,
  Versions
} from 'pages';
import App from './App';
import i18n from './i18n';
import './index.css';

// eslint-disable-next-line no-unused-vars
const l = i18n.language;

if (process.env.REACT_APP_USE_SENTRY === 'true') {
  Sentry.init({
    dsn: 'https://68356550e6524904befafcde576c3c24@o1279236.ingest.sentry.io/6488595',
    environment: process.env.NODE_ENV,
    attachStacktrace: true,
    integrations: [
      new Sentry.BrowserTracing(),
      new Sentry.Integrations.Breadcrumbs({
        console: false
      })
    ],
    beforeSend: (event) => {
      // Only send prod events
      if (process.env.NODE_ENV === 'production') {
        return event;
      } else {
        return null;
      }
    },
    denyUrls: [
      // Chrome extensions
      /extensions\//i,
      /^chrome:\/\//i
    ],

    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: 1.0
  });
}

const registerBrowserLanguage = () => {
  registerLanguage((window.navigator.language || window.navigator.userLanguage).split('-')[0]);
};

window.addEventListener('languagechange', registerBrowserLanguage);
registerBrowserLanguage();

// No need to register regional variants of languages (like sv-fi) because the library doesn't support them
registerLanguages([ 'en', 'sv', 'es', 'no', 'fi', 'da' ]);

const root = createRoot(document.getElementById('root'));
root.render(
  <BrowserRouter>
    <Route
      path="/:id?"
      render={() => (
        <AppProvider>
          <BillingProvider>
            <TransactionProvider>
              <DialogNavigationProvider>
                <SignProvider>
                  <SepaProvider>
                    <AutogiroProvider>
                      <BetalingsserviceProvider>
                        <AuthProvider>
                          <ThemeProvider theme={theme}>
                            <LocaleProvider>
                              <PaymentProvider>
                                <Switch>
                                  <Route path="/versions" component={Versions} />
                                  <Route path="/:id/download/:file" component={Download} />
                                  <Route path="/:id?/:step?" component={App} />
                                </Switch>
                              </PaymentProvider>
                            </LocaleProvider>
                          </ThemeProvider>
                        </AuthProvider>
                      </BetalingsserviceProvider>
                    </AutogiroProvider>
                  </SepaProvider>
                </SignProvider>
              </DialogNavigationProvider>
            </TransactionProvider>
          </BillingProvider>
        </AppProvider>
      )}
    />
  </BrowserRouter>
);
